import { Switch, TextField, Typography } from "@mui/material"
import { Box } from "@mui/system"
import { useCallback, useEffect, useState } from "react";

type Month = {
    tjm: number;
    amount: number;
    openDays: number;
};

const TurnoverMonthFields = ({
    formik,
    monthList,
    tjm
}: {
    formik: any,
    monthList: { name: string, field: string }[],
    tjm: number
}) => {
    const label = { inputProps: { 'aria-label': 'Switch demo' } };

    const [typeChecked, setTypeChecked] = useState<boolean>(false);
    const [fixedTjm, setFixedTjm] = useState<boolean>(false);
    const [amount, setAmount] = useState<number>(0);
    const [tjmAmount, setTjmAmount] = useState<number>(tjm);






    useEffect(() => {
        if (formik.values.monthlyAmount) {
            setTjmAmount(formik.values.monthlyAmount);
            setAmount(formik.values.monthlyAmount);
        } else {
            setTjmAmount(0);
            setAmount(0);
        }
    }, [formik.values.monthlyAmount]);

    useEffect(() => {
        if (formik.values.type === "Régie") {
            setFixedTjm(!formik.values.months.every((month: Month) => month.tjm === formik.values.months[0].tjm));
        } else {
            setTypeChecked(!formik.values.months.every((month: Month) => month.amount === formik.values.months[0].amount));
        }
    }, [formik.values.months, formik.values.type]);



    const handleFixedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        
        // Si vide, on met 0, sinon on parse en float
        const parsedValue = value === "" ? 0 : parseFloat(value);

        if (formik.values.type === "Régie") {
            setTjmAmount(parsedValue);
        } else {
            setAmount(parsedValue);
        }

        // Mise à jour de tous les mois dans Formik
        formik.setFieldValue(
            "months",
            formik.values.months.map((month: Month) => ({
                ...month,
                [name]: parsedValue, // 'tjm' ou 'amount'
            }))
        );
    };



    const handleSwitchChange = () => {
        if (formik.values.type === "Régie") {
            const nextState = !fixedTjm;
            setFixedTjm(nextState);
            // Si on passe en mode constant (basé sur ton switch)
            if (!nextState) {
                const updated = formik.values.months.map((m: any) => ({ ...m, tjm: tjmAmount }));
                formik.setFieldValue("months", updated);
            }
        } else {
            const nextState = !typeChecked;
            setTypeChecked(nextState);
            if (!nextState) {
                const updated = formik.values.months.map((m: any) => ({ ...m, amount: amount }));
                formik.setFieldValue("months", updated);
            }
        }
    };

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const [_, index, field] = name.split("-");

        // Sécurité : si l'utilisateur efface tout, on stocke 0 dans Formik
        const finalValue = value === "" ? 0 : Number(value);
        
        formik.setFieldValue(`months.${index}.${field}`, finalValue);
    }, [formik]);


    return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start"}}>
            

            <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: 2, 
                p: 1,
                bgcolor: "#f1f5f9",
                borderRadius: 10 // Look pilule
            }}>
                <Typography variant="body2" sx={{ fontWeight: !typeChecked ? 'bold' : 'normal' }}>Montant constant</Typography>
                <Switch  checked={(formik.values.type === "Régie" ? fixedTjm : typeChecked) || false} onChange={handleSwitchChange} />
                <Typography variant="body2" sx={{ fontWeight: typeChecked ? 'bold' : 'normal' }}>Montant variable</Typography>
            </Box>
            {(formik.values.type === "Régie" ? !fixedTjm : !typeChecked) && (
                <TextField
                    margin="normal"
                    required
                    type="number"
                    inputProps={{ step: "0.1", inputMode: "decimal" }}
                    id={formik.values.type === "Régie" ? "tjm" : "amount"}
                    label={formik.values.type === "Régie" ? "TJM Mensuel" : "Montant Mensuel"}
                    name={formik.values.type === "Régie" ? "tjm" : "amount"}
                    // Correction ici : On utilise les états locaux 'tjmAmount' ou 'amount'
                    value={(formik.values.type === "Régie" ? tjmAmount : amount) || ""} 
                    onChange={handleFixedChange}
                    onBlur={(e) => {
                        if (e.target.value === "") {
                            // Si l'utilisateur sort du champ vide, on remet 0 partout
                            const name = formik.values.type === "Régie" ? "tjm" : "amount";
                            if (formik.values.type === "Régie") setTjmAmount(0); else setAmount(0);
                            
                            formik.setFieldValue(
                                "months",
                                formik.values.months.map((month: Month) => ({ ...month, [name]: 0 }))
                            );
                        }
                    }}
                    sx={{ width: "40%", marginInline: "auto" }}
                />
            )}
            <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", // Aligne proprement les mois
                gap: 2, 
                mt: 2 
            }}>
                {
                    monthList.map((month, i) => {
                        if (formik.values.type === "Régie") {
                            return (
                                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center"}}>
                                    <Typography variant="subtitle1" sx={{ textAlign: "center" }}>{month.name}</Typography>
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <TextField
                                            margin="normal"
                                            required
                                            type="number"
                                            id={`month-${i}-openDays`}
                                            label={"Jours ouvrés"}
                                            name={`month-${i}-openDays`}
                                            autoComplete={`month-${i}-openDays`}
                                            value={formik.values.months[i].openDays}
                                            onChange={handleChange}
                                            onBlur={(e) => {
                                                if (e.target.value === "") {
                                                    formik.setFieldValue(`months.${i}.tjm`, 0);
                                                }
                                            }}
                                            disabled={!fixedTjm}
                                            sx={{ width: "45%" }}
                                        />
                                        <TextField
                                            margin="normal"
                                            required
                                            type="number"
                                            id={`month-${i}-tjm`}
                                            label={"TJM"}
                                            name={`month-${i}-tjm`}
                                            autoComplete={`month-${i}-tjm`}
                                            value={formik.values.months[i].tjm}
                                            onChange={handleChange}
                                            autoFocus
                                            disabled={!fixedTjm}
                                            sx={{ width: "45%" }}
                                        />
                                    </Box>
                                </Box>
                            )
                        }
                        else {
                            return (
                                <TextField
                                    margin="normal"
                                    required
                                    type="number"
                                    id={`month-${i}-amount`}
                                    label={month.name}
                                    name={`month-${i}-amount`}
                                    autoComplete={`month-${i}-amount`}
                                    value={formik.values.months[i].amount}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        if (e.target.value === "") {
                                            formik.setFieldValue(`months.${i}.amount`, 0);
                                        }
                                    }}
                                    disabled={!typeChecked}
                                />
                            )
                        }
                    })
                }
            </Box>
        </Box>
    )
}

export default TurnoverMonthFields;